/**
 * Issue #183/#184 — 系统设置 API 客户端（课程分类 / 人力资源配置 / 部门 CRUD）。
 *
 * 全部经 apiClient：自动注入 Bearer、强制校验 response.ok + content-type，非 JSON /
 * 非 2xx / 业务非零 code 一律抛 ApiError（带 HTTP 状态），UI 据此展示明确错误，
 * 绝不出现「Unexpected token」、不塞前端假数据。
 */
import { apiClient } from './api';
import type { DeleteCaptchaProof } from './captcha-api';
import type { AdminDepartmentItem, CourseCategoriesEnvelope, HrConfig } from './types';

// ─── 课程分类（KPI考核） ───
export async function getCourseCategoriesApi(): Promise<string[]> {
  const data = await apiClient.get<CourseCategoriesEnvelope>('/system/course-categories');
  return data?.categories ?? [];
}

export async function setCourseCategoriesApi(categories: string[]): Promise<string[]> {
  const data = await apiClient.patch<CourseCategoriesEnvelope>('/system/course-categories', {
    categories,
  });
  return data?.categories ?? [];
}

// ─── 人力资源配置（岗位 / 默认密码 / 默认头像） ───
export function getHrConfigApi(): Promise<HrConfig> {
  return apiClient.get<HrConfig>('/system/hr-config');
}

export function updateHrConfigApi(
  // Issue #235：删除/移除岗位时随 body 携带后端签发的一次性图形验证码（position:delete）。
  // 纯新增/重排不带 proof；后端只在检测到已存在岗位消失时强制校验。
  body: Partial<Pick<HrConfig, 'positions' | 'roles' | 'defaultPassword' | 'defaultAvatarUrl'>> &
    Partial<DeleteCaptchaProof>,
): Promise<HrConfig> {
  return apiClient.patch<HrConfig>('/system/hr-config', body);
}

// ─── 部门管理（真实 Department CRUD） ───
export function createDepartmentApi(name: string): Promise<AdminDepartmentItem> {
  return apiClient.post<AdminDepartmentItem>('/admin/departments', { name });
}

export function updateDepartmentApi(id: string, name: string): Promise<AdminDepartmentItem> {
  return apiClient.patch<AdminDepartmentItem>(
    `/admin/departments/${encodeURIComponent(id)}`,
    { name },
  );
}

export function deleteDepartmentApi(
  id: string,
  // Issue #235：删除需携带 6 位图形验证码，由后端一次性校验。
  proof: DeleteCaptchaProof,
): Promise<{ id: string; deleted: boolean; activeEmployees: number }> {
  return apiClient.delete<{ id: string; deleted: boolean; activeEmployees: number }>(
    `/admin/departments/${encodeURIComponent(id)}`,
    proof,
  );
}
